import React, { useEffect, useState } from "react";
import {
  BucketGroupFieldNames,
  IPurchPriceProfile,
  PricingTemplate,
} from "../../../../interfaces/partner.interface";
import { Button, Form, Input, InputNumber, Radio, Select, Space } from "antd";
import styles from "./CreateEditBucketGroupsForm.module.css";
import {
  PartnerBucketGroups,
  ValueType,
} from "../../../../interfaces/partnerDeal.interface";
import { DEFAULT_AMEX_BUCKET } from "../../../common/constants/defaultAmexBucket";
import {
  reconcileAmexBucketGroupForSave,
  withoutInPersonAmexBucket,
} from "../../../common/constants/inPersonAmexVisibility";
import { useAppSelector } from "../../../../app/hooks";
import { selectHideInPersonAmex } from "../../../feature-flags/featureFlagsSlice";

export const valueTypeLabel = (valueType?: ValueType) => {
  let label = valueType === "Percentage" ? "%" : valueType;
  return <span>{label}</span>;
};
const partnerDealsFormFieldLayout = {
  labelCol: { span: 9 },
  wrapperCol: { span: 18 },
};

interface IBucketGroupsForm {
  purchPriceProfiles?: IPurchPriceProfile[];
  bucketGroupsToEdit?: PartnerBucketGroups;
  purchPriceProfileToEdit?: IPurchPriceProfile;
  disableProfileSelection?: boolean;
  target?: "defaultPrice" | "partnerDeal" | "softwareVendorPrice";
  action?: "create" | "edit";

  onSubmit: (value: {
    purchPriceProfileId: string;
    bucketGroups: PartnerBucketGroups;
  }) => void;
}
export default function CreateEditBucketGroupsForm({
  purchPriceProfiles,
  onSubmit,
  bucketGroupsToEdit,
  purchPriceProfileToEdit,
  disableProfileSelection,
  target,
  action,
}: IBucketGroupsForm) {
  const [form] = Form.useForm();
  const hideInPersonAmex = useAppSelector(selectHideInPersonAmex);
  const [purchPriceProfile, setPurchPriceProfile] = useState(
    purchPriceProfileToEdit
      ? { ...purchPriceProfileToEdit }
      : purchPriceProfiles?.[0]
  );

  useEffect(() => {
    form.resetFields();
    populateForm();
    console.log("purchPriceProfile", purchPriceProfile);
  }, [
    JSON.stringify(purchPriceProfiles),
    JSON.stringify(bucketGroupsToEdit),
    hideInPersonAmex,
  ]);

  const populateForm = () => {
    const source = bucketGroupsToEdit
      ? { ...bucketGroupsToEdit }
      : purchPriceProfile?.bucketGroups
        ? { ...purchPriceProfile.bucketGroups }
        : undefined;

    let bucketGroupsValue = source;
    if (source && !hideInPersonAmex) {
      bucketGroupsValue = {
        ...source,
        amex:
          source.amex && source.amex.valueType === "Percentage"
            ? source.amex
            : {
                value: DEFAULT_AMEX_BUCKET.value,
                valueType: "Percentage",
              },
      };
    } else if (source && hideInPersonAmex) {
      bucketGroupsValue = withoutInPersonAmexBucket(source);
    }

    form.setFieldsValue({
      name: purchPriceProfile?.name,
      bucketGroups: bucketGroupsValue,
      purchPriceProfileId:
        purchPriceProfileToEdit?._id || purchPriceProfiles?.[0]?._id,
    });
  };

  const handlePurchPriceProfileChange = (purchPriceProfileId: string) => {
    let purchPriceProfile = purchPriceProfiles?.find(
      (pp) => pp._id === purchPriceProfileId
    );
    if (purchPriceProfile) setPurchPriceProfile(purchPriceProfile);
  };

  // The value type is read-only here (dictated by the Rapyd price range). This is
  // the single source of truth used both for the displayed label and the submitted
  // value, so the two can't diverge. AMEX is always Percentage (PC-757): never
  // trust a stored Fixed value type on it.
  const resolveValueType = (type: BucketGroupFieldNames): ValueType =>
    type === "amex"
      ? "Percentage"
      : (purchPriceProfile?.bucketGroups?.[type]?.valueType ??
        bucketGroupsToEdit?.[type]?.valueType ??
        (type === "domesticDebitCards" || type === "domesticCreditCards"
          ? "Fixed"
          : "Percentage"));

  // PC-757: AMEX always renders even when the profile has no amex range, so fall
  // back to the canonical 3.3% / 4% bucket for its help text and min/max bounds.
  const resolveRange = (type: BucketGroupFieldNames) =>
    type === "amex"
      ? purchPriceProfile?.bucketGroups?.amex?.valueType === "Percentage"
        ? purchPriceProfile?.bucketGroups?.amex
        : DEFAULT_AMEX_BUCKET
      : purchPriceProfile?.bucketGroups?.[type];

  const handleFinish = (value: {
    purchPriceProfileId: string;
    bucketGroups: PartnerBucketGroups;
  }) => {
    // Force each submitted bucket's valueType to the authoritative type used for
    // display, so the persisted type can't diverge from the UI (e.g. a newly added
    // AMEX group showing FIXED but being saved as Percentage).
    if (value?.bucketGroups) {
      (Object.keys(value.bucketGroups) as BucketGroupFieldNames[]).forEach(
        (type) => {
          const bucket = value.bucketGroups[type];
          if (bucket) bucket.valueType = resolveValueType(type);
        }
      );
      value.bucketGroups = reconcileAmexBucketGroupForSave(
        value.bucketGroups,
        bucketGroupsToEdit,
        hideInPersonAmex
      ) as PartnerBucketGroups;
    }
    return onSubmit(value);
  };

  const PricingFormItem = (
    type:
      | "domesticDebitCards"
      | "domesticCreditCards"
      | "europeanDebitCards"
      | "european"
      | "internationalAndCommercial"
      | "amex"
  ) => {
    const resolvedValueType = resolveValueType(type);
    const range = resolveRange(type);
    let label =
      type === "domesticDebitCards"
        ? "Domestic Debit Cards"
        : type === "domesticCreditCards"
          ? "Domestic Credit Cards"
          : type === "europeanDebitCards"
            ? "European Debit Cards"
            : type === "european"
              ? "E.E.A Cards"
              : type === "amex"
                ? "AMEX"
                : "Non E.E.A & Corporate Cards";
    let toolTipText =
      type === "domesticDebitCards"
        ? "Includes domestic debit cards only."
        : type === "domesticCreditCards"
          ? "Includes domestic credit cards only."
          : type === "europeanDebitCards"
            ? "Includes all European debit cards that are not commerical or international."
            : type === "amex"
              ? "American Express card-present (in-person) transactions."
              : type === "european"
                ? purchPriceProfile?.pricingTemplate ===
                  PricingTemplate.DomesticCreditSplit
                  ? `Includes all European credit cards (and European debit, as long as no european debit group is set) that are not commercial or international. Excludes domestic credit cards.`
                  : `Includes all European credit cards (and European debit, as long as no european debit group is set) that are not commerical or international as well as all domestic credit cards.`
                : "Includes all commerical & international debit and credit cards.";
    return (
      <>
        <Form.Item
          label={label}
          name={["bucketGroups", type, "value"]}
          labelCol={{ ...partnerDealsFormFieldLayout.labelCol }}
          labelAlign="left"
          rules={[{ required: true, message: "!*" }]}
          tooltip={toolTipText}
          help={
            purchPriceProfile
              ? `Partner Purchase Price ${range?.value} | Max Total Price ${range?.max}`
              : ""
          }
          className={styles.bucket_group_form_item}
        >
          <InputNumber
            controls={false}
            className={styles.bucket_value_input}
            min={purchPriceProfile ? range?.value : Number.NEGATIVE_INFINITY}
            max={purchPriceProfile ? range?.max : Number.POSITIVE_INFINITY}
            style={{ width: "140px" }}
            addonAfter={valueTypeLabel(resolvedValueType)}
          />
        </Form.Item>

        <Form.Item
          hidden
          name={["bucketGroups", type, "valueType"]}
          initialValue={resolvedValueType}
        >
          <Input />
        </Form.Item>
      </>
    );
  };

  return (
    <div className={styles.container}>
      <div>
        <Form
          onFinish={handleFinish}
          form={form}
          style={{
            minWidth: 600,
          }}
        >
          {purchPriceProfiles && purchPriceProfiles?.length > 0 && (
            <Form.Item
              label={"Rapyd Price Range"}
              name={["purchPriceProfileId"]}
              labelCol={{ ...partnerDealsFormFieldLayout.labelCol }}
              labelAlign="left"
              hidden={purchPriceProfiles?.length === 1}
            >
              <Select
                style={{ width: "200px" }}
                onChange={handlePurchPriceProfileChange}
                disabled={disableProfileSelection}
                options={purchPriceProfiles?.map((pp) => ({
                  value: pp._id,
                  label: pp.name,
                }))}
              ></Select>
            </Form.Item>
          )}

          {PricingFormItem("domesticDebitCards")}
          {(purchPriceProfile?.pricingTemplate ===
            PricingTemplate.EuropeanDebitSplit ||
            bucketGroupsToEdit?.europeanDebitCards) &&
            PricingFormItem("europeanDebitCards")}

          {(purchPriceProfile?.pricingTemplate ===
            PricingTemplate.DomesticCreditSplit ||
            bucketGroupsToEdit?.domesticCreditCards) &&
            PricingFormItem("domesticCreditCards")}

          {PricingFormItem("european")}

          {PricingFormItem("internationalAndCommercial")}

          {!hideInPersonAmex && PricingFormItem("amex")}

          <Form.Item>
            <Button style={{ float: "right" }} type="primary" htmlType="submit">
              OK
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
}

{
  /* 
          <Form.List name={"buckets"}>
            {(fields) => (
              <div>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} align="baseline">
                    <Form.Item {...restField} name={[name, "name"]}>
                      <Input
                        className={styles.bucket_name_input}
                        disabled
                      ></Input>
                    </Form.Item>

                    <Form.Item {...restField} name={[name, "value"]}>
                      <InputNumber
                        controls={false}
                        className={styles.bucket_value_input}
                        min={0}
                      />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, "valueType"]}>
                      <Select
                        className={styles.bucket_value_input}
                        style={{ width: "85px" }}
                      >
                        <Select.Option value={"Percentage"}>%</Select.Option>
                        <Select.Option value={"Fixed"}>FIXED</Select.Option>
                      </Select>
                    </Form.Item>
                    <Form.Item {...restField} name={[name, "max"]}>
                      <InputNumber
                        controls={false}
                        className={styles.bucket_value_input}
                        min={0}
                        max={3}
                      />
                    </Form.Item>
                  </Space>
                ))}
              </div>
            )}
          </Form.List>  */
}
